import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { NotificationScheduleDateIcon } from "../../styles/icons";
import DateUtil from "../../util/DateUtil";

interface DateRangeFieldProps {
  label?: string;
  startDate?: string;
  endDate?: string;
  onStartDateChange: (value: string | undefined) => void;
  onEndDateChange: (value: string | undefined) => void;
  placeholder?: string;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

enum DateRangePreset {
  Today = "today",
  Last7Days = "last7Days",
  Last30Days = "last30Days",
}

const DATE_RANGE_PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: DateRangePreset.Today, label: "Today" },
  { id: DateRangePreset.Last7Days, label: "Last 7 Days" },
  { id: DateRangePreset.Last30Days, label: "Last 30 Days" },
];

const getPresetRange = (
  preset: DateRangePreset
): { start: string; end: string } => {
  const today = new Date();
  const end = DateUtil.getTimezoneAgnosticDateFormat(
    DateUtil.dateToTimezoneAgnosticISO(today)
  );

  switch (preset) {
    case DateRangePreset.Today:
      return { start: end, end };
    case DateRangePreset.Last7Days:
      return {
        start: DateUtil.getTimezoneAgnosticDateFormat(
          DateUtil.dateToTimezoneAgnosticISO(subDays(today, 6))
        ),
        end,
      };
    case DateRangePreset.Last30Days:
      return {
        start: DateUtil.getTimezoneAgnosticDateFormat(
          DateUtil.dateToTimezoneAgnosticISO(subDays(today, 29))
        ),
        end,
      };
  }
};

const getActivePreset = (
  startDate?: string,
  endDate?: string
): DateRangePreset | undefined => {
  if (!startDate || !endDate) {
    return undefined;
  }

  return DATE_RANGE_PRESETS.find((preset) => {
    const range = getPresetRange(preset.id);
    return range.start === startDate && range.end === endDate;
  })?.id;
};

const getDisplayValue = (
  startDate?: string,
  endDate?: string,
  placeholder?: string
): string => {
  if (startDate && endDate) {
    if (startDate === endDate) {
      return DateUtil.getFormattedDateFromUtcToLocalTimezone(startDate);
    }
    return `${DateUtil.getFormattedDateFromUtcToLocalTimezone(
      startDate
    )} – ${DateUtil.getFormattedDateFromUtcToLocalTimezone(endDate)}`;
  }
  if (startDate) {
    return `${DateUtil.getFormattedDateFromUtcToLocalTimezone(startDate)} –`;
  }
  return placeholder ?? "";
};

export const DateRangeField = ({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  placeholder = "Select date range",
}: DateRangeFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(
    () =>
      (startDate &&
        DateUtil.timezoneAgnosticISOToDate(`${startDate}T00:00:00`)) ||
      new Date()
  );
  const [hoverDate, setHoverDate] = useState<string>();

  const hasValue = Boolean(startDate || endDate);
  const displayValue = getDisplayValue(startDate, endDate);
  const activePreset = getActivePreset(startDate, endDate);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(visibleMonth);
    const monthEnd = endOfMonth(visibleMonth);
    const intervalStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const intervalEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: intervalStart, end: intervalEnd });
  }, [visibleMonth]);

  const previewEndDate =
    startDate && !endDate && hoverDate && hoverDate >= startDate
      ? hoverDate
      : endDate;

  const clearRange = (event: React.MouseEvent) => {
    event.stopPropagation();
    onStartDateChange(undefined);
    onEndDateChange(undefined);
    setHoverDate(undefined);
  };

  const applyPreset = (preset: DateRangePreset) => {
    const range = getPresetRange(preset);
    onStartDateChange(range.start);
    onEndDateChange(range.end);
    setVisibleMonth(
      DateUtil.timezoneAgnosticISOToDate(`${range.start}T00:00:00`) ??
        new Date()
    );
    setHoverDate(undefined);
    setIsOpen(false);
  };

  const handleDayClick = (day: Date) => {
    const clicked = DateUtil.getTimezoneAgnosticDateFormat(
      DateUtil.dateToTimezoneAgnosticISO(day)
    );

    if (!startDate || (startDate && endDate)) {
      onStartDateChange(clicked);
      onEndDateChange(undefined);
      setHoverDate(undefined);
      return;
    }

    if (clicked < startDate) {
      onEndDateChange(startDate);
      onStartDateChange(clicked);
    } else {
      onEndDateChange(clicked);
    }

    setHoverDate(undefined);
    setIsOpen(false);
  };

  const getDayStyles = (day: Date) => {
    const dayString = DateUtil.getTimezoneAgnosticDateFormat(
      DateUtil.dateToTimezoneAgnosticISO(day)
    );
    const isStart = startDate === dayString;
    const isEnd = endDate === dayString;
    const isPreviewEnd = previewEndDate === dayString && !endDate;
    const isSelected = isStart || isEnd || isPreviewEnd;

    const rangeStart = startDate
      ? DateUtil.timezoneAgnosticISOToDate(`${startDate}T00:00:00`) ?? undefined
      : undefined;
    const rangeEnd = previewEndDate
      ? DateUtil.timezoneAgnosticISOToDate(`${previewEndDate}T00:00:00`) ??
        undefined
      : undefined;
    const isInRange =
      rangeStart &&
      rangeEnd &&
      isWithinInterval(day, {
        start: rangeStart <= rangeEnd ? rangeStart : rangeEnd,
        end: rangeStart <= rangeEnd ? rangeEnd : rangeStart,
      });

    return {
      bg: isSelected
        ? "cherryButton.500"
        : isInRange
        ? "cherryButton.50"
        : "transparent",
      color: isSelected
        ? "white"
        : isSameMonth(day, visibleMonth)
        ? "inherit"
        : "cherryGrey.400",
      fontWeight: isSelected ? "semibold" : "normal",
      borderRadius:
        isStart || isEnd || isPreviewEnd ? "md" : isInRange ? "none" : "md",
    };
  };

  return (
    <FormControl>
      {label && <FormLabel>{label}</FormLabel>}
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        closeOnBlur
      >
        <PopoverTrigger>
          <InputGroup backgroundColor="#fff" shadow="sm">
            <Input
              readOnly
              value={displayValue}
              placeholder={placeholder}
              cursor="pointer"
              onClick={() => setIsOpen(true)}
            />
            <InputRightElement width="2.5rem">
              {hasValue ? (
                <CloseIcon
                  cursor="pointer"
                  boxSize={3}
                  color="cherryGrey.500"
                  onClick={clearRange}
                />
              ) : (
                <NotificationScheduleDateIcon color="cherryGrey.500" />
              )}
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent width="auto" _focus={{ outline: "none" }}>
          <PopoverBody p={3}>
            <Flex gap={2} mb={3} flexWrap="wrap">
              {DATE_RANGE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  size="xs"
                  variant={activePreset === preset.id ? "solid" : "outline"}
                  colorScheme="cherryButton"
                  onClick={() => applyPreset(preset.id)}
                >
                  {preset.label}
                </Button>
              ))}
            </Flex>
            <Flex alignItems="center" justifyContent="space-between" mb={3}>
              <IconButton
                aria-label="Previous month"
                icon={<ChevronLeftIcon />}
                size="sm"
                variant="ghost"
                onClick={() => setVisibleMonth((month) => subMonths(month, 1))}
              />
              <Text fontWeight="semibold" fontSize="sm">
                {format(visibleMonth, "MMMM yyyy")}
              </Text>
              <IconButton
                aria-label="Next month"
                icon={<ChevronRightIcon />}
                size="sm"
                variant="ghost"
                onClick={() => setVisibleMonth((month) => addMonths(month, 1))}
              />
            </Flex>
            <Box
              display="grid"
              gridTemplateColumns="repeat(7, 2rem)"
              gap={1}
              justifyContent="center"
            >
              {WEEKDAY_LABELS.map((weekday) => (
                <Text
                  key={weekday}
                  fontSize="xs"
                  fontWeight="semibold"
                  color="cherryGrey.500"
                  textAlign="center"
                >
                  {weekday}
                </Text>
              ))}
              {calendarDays.map((day) => {
                const dayString = DateUtil.getTimezoneAgnosticDateFormat(
                  DateUtil.dateToTimezoneAgnosticISO(day)
                );
                const dayStyles = getDayStyles(day);

                return (
                  <Box
                    key={dayString}
                    as="button"
                    type="button"
                    width="2rem"
                    height="2rem"
                    fontSize="sm"
                    lineHeight="2rem"
                    textAlign="center"
                    cursor="pointer"
                    transition="background-color 0.15s ease"
                    _hover={{
                      bg:
                        dayStyles.bg === "transparent"
                          ? "cherryGrey.100"
                          : dayStyles.bg,
                    }}
                    onMouseEnter={() => {
                      if (startDate && !endDate) {
                        setHoverDate(dayString);
                      }
                    }}
                    onMouseLeave={() => setHoverDate(undefined)}
                    onClick={() => handleDayClick(day)}
                    {...dayStyles}
                  >
                    {format(day, "d")}
                  </Box>
                );
              })}
            </Box>
            {startDate && !endDate && (
              <Text mt={3} fontSize="xs" color="cherryGrey.500">
                Select an end date
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};
